import { Component, useState, type ErrorInfo, type ReactNode } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import posthog from "@/lib/posthog";

export type FallbackArgs = {
  /** Clears the error and re-renders children. */
  reset: () => void;
  error: Error;
  /** Plain-text report: message, stacks, URL, browser. Ready to paste. */
  details: string;
};

type Props = {
  children: ReactNode;
  /** Rendered instead of children when a descendant throws. */
  fallback?: (args: FallbackArgs) => ReactNode;
  /** Changing this value clears the error and re-renders children. */
  resetKey?: unknown;
  /** Tag attached to the PostHog event so we can tell the surfaces apart. */
  surface: string;
};

type State = { error: Error | null; componentStack: string | null };

const formatDetails = (error: Error, componentStack: string | null, surface: string): string =>
  [
    "Palomma — reporte de error",
    `Fecha: ${new Date().toISOString()}`,
    `Sección: ${surface}`,
    `URL: ${window.location.href}`,
    `Navegador: ${navigator.userAgent}`,
    "",
    `Mensaje: ${error.message}`,
    "",
    "Stack:",
    error.stack ?? "(sin stack)",
    "",
    "Component stack:",
    componentStack ?? "(sin component stack)",
  ].join("\n");

/** Copies the full report; the visible card shows only the message, which is what
 *  fits in a screenshot. */
export const CopyDetailsButton = ({ details }: { details: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context or denied permission) — the message
      // on screen is still screenshot-able, so fail quietly.
    }
  };

  return (
    <Button size="sm" variant="outline" className="min-h-11" onClick={handleCopy}>
      {copied ? "Copiado" : "Copiar detalles"}
    </Button>
  );
};

const FullPageFallback = ({ reset, error, details }: FallbackArgs) => (
  <div className="flex min-h-dvh items-center justify-center bg-[#F9F9FB] p-6">
    <Alert status="error" className="w-full max-w-lg">
      <p className="text-sm font-semibold">Algo salió mal</p>
      <p className="mt-1 text-sm">
        Ocurrió un error inesperado. Copia los detalles y compártelos con el equipo de Palomma.
      </p>
      <pre className="mt-2 max-h-32 overflow-auto rounded border border-red-200 bg-white/70 p-2 text-xs whitespace-pre-wrap break-words">
        {error.message}
      </pre>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" className="min-h-11" onClick={() => window.location.reload()}>
          Recargar
        </Button>
        <Button size="sm" variant="outline" className="min-h-11" onClick={reset}>
          Reintentar
        </Button>
        <CopyDetailsButton details={details} />
      </div>
    </Alert>
  </div>
);

/**
 * Catches render and commit errors so a crash degrades to a fallback instead of
 * unmounting the whole React root (which renders a blank page). Class component
 * because that is the only way to catch these in React 18.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError = (error: Error): Partial<State> => ({ error });

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.handleReset();
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const componentStack = info.componentStack ?? null;
    console.error(`[ErrorBoundary:${this.props.surface}]`, error, componentStack);
    this.setState({ componentStack });
    // posthog.init only runs when VITE_POSTHOG_KEY is set (prod), so guard.
    if (posthog.__loaded) {
      posthog.captureException(error, { surface: this.props.surface, componentStack });
    }
  }

  handleReset = () => this.setState({ error: null, componentStack: null });

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    const args: FallbackArgs = {
      reset: this.handleReset,
      error,
      details: formatDetails(error, componentStack, this.props.surface),
    };
    return this.props.fallback ? this.props.fallback(args) : <FullPageFallback {...args} />;
  }
}

export default ErrorBoundary;
